import { useState, useEffect, useContext } from "react";
import Title from "../component/Title";
import GlobalContext from "../GlobalContext";
import Axios from "axios";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Checkbox,
} from "@mui/material";
import Select from "@mui/material/Select";

const VideosAdmin = () => {
  const { globalURL } = useContext(GlobalContext);
  const url = globalURL;
  const [loginStatus, setLoginStatus] = useState(false);

  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDesc] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");

  // ✅ New state for spotlight mode
  const [editSpotlights, setEditSpotlights] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const handleResponse = (msg, variant) => enqueueSnackbar(msg, { variant });

  Axios.defaults.withCredentials = true;

  // Extract YouTube video ID from any link format
  const getYouTubeId = (url) => {
    const match = url.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    return match ? match[1] : null;
  };

  // Format date (MM/DD/YYYY)
  const formatDate = (date) => dayjs(date).format("MM/DD/YYYY");

  // Fetch all videos
  const fetchVideos = () => {
    Axios.get(url + "/videos")
      .then((response) => setVideos(response.data))
      .catch(() => handleResponse("Error fetching videos.", "error"));
  };

  // Create YouTube video
  const postVideo = async () => {
    const videoId = getYouTubeId(youtubeUrl);
    if (!videoId) {
      handleResponse("Invalid YouTube link.", "error");
      return;
    }

    await Axios.post(url + "/videos", {
      title,
      description,
      url: youtubeUrl,
    })
      .then(() => {
        fetchVideos();
        handleResponse("YouTube video added successfully!", "success");
        setTitle("");
        setDesc("");
        setYoutubeUrl("");
      })
      .catch(() => handleResponse("Error adding video.", "error"));
  };

  // Delete video
  const deleteVideo = async () => {
    await Axios.delete(url + "/videos/" + selectedVideo)
      .then(() => {
        setSelectedVideo("");
        fetchVideos();
        handleResponse("Video deleted successfully!", "success");
      })
      .catch(() => handleResponse("Error deleting video.", "error"));
  };

  // ✅ Toggle spotlight for one video
  const toggleSpotlight = async (videoId, newValue) => {
    await Axios.patch(`${url}/videos/${videoId}/spotlight`, {
      spotlight: newValue,
    })
      .then(() => {
        fetchVideos();
      })
      .catch(() => handleResponse("Error updating spotlight.", "error"));
  };

  // ✅ Reset all spotlights
  const resetSpotlights = async () => {
    await Axios.patch(`${url}/videos/reset/all-spotlights`)
      .then(() => {
        fetchVideos();
        handleResponse("All spotlights have been reset!", "info");
      })
      .catch(() => handleResponse("Error resetting spotlights.", "error"));
  };

  useEffect(() => {
    Axios.get(url + "/users/login", {
      headers: {
        "auth-token": localStorage.getItem("token"),
      },
    }).then((response) => {
      setLoginStatus(response.data.LoggedIn ? true : false);
    });
    fetchVideos();
  }, []);

  // Separate spotlight videos
  const spotlightVideos = videos.filter((v) => v.spotlight);

  return (
    <div className="min-h-screen">
      {loginStatus && (
        <div className="container">
          {/* === HEADER === */}
          <div className="bgRed sm:w-[85%] lg:w-[60%] relative flex justify-center flex-col items-center p-4 border-black border-8 rounded-3xl my-8 sm-shadow">
            <Title word="YouTube Videos" />
          </div>

          {/* === ADD YOUTUBE VIDEO === */}
          <div className="content my-8 justify-start items-center sm:w-[95%] lg:w-[80%] sm:px-4 lg:px-10 pt-10 pb-10 black-bg border-black border-8">
            <div className="relative text-center w-fit mb-8 text-4xl font-extrabold stats-font p-2">
              Add YouTube Video
              <span className="absolute w-[100%] h-1 bottom-0 left-[-50%] translate-x-[50%] rounded-full bg-white"></span>
            </div>

            <div className="flex gap-4 flex-row w-full justify-evenly items-center">
              {/* LEFT FORM */}
              <div className="flex w-1/3 flex-col my-4 gap-2">
                <TextField
                  label="Title"
                  onChange={(e) => setTitle(e.target.value)}
                  value={title}
                />
                <TextField
                  label="Description"
                  onChange={(e) => setDesc(e.target.value)}
                  value={description}
                />
                <TextField
                  label="YouTube Link"
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  value={youtubeUrl}
                />
                <Button variant="outlined" onClick={postVideo}>
                  ADD
                </Button>
              </div>

              {/* RIGHT PREVIEW */}
              <div className="flex justify-center items-center sm:w-[250px] lg:w-[500px] sm:h-[250px] lg:h-[500px] border-4 border-white rounded-xl overflow-hidden bg-black">
                {getYouTubeId(youtubeUrl) ? (
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${getYouTubeId(
                      youtubeUrl
                    )}`}
                    title="YouTube preview"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <div className="text-white text-center">YouTube Preview</div>
                )}
              </div>
            </div>
          </div>

          {/* === SPOTLIGHT VIDEOS === */}
          {spotlightVideos.length > 0 && (
            <div className="content my-8 justify-start items-center sm:w-[95%] lg:w-[80%] sm:px-4 lg:px-10 pt-10 pb-10 border-yellow-400 border-8 rounded-3xl bg-[#111]">
              <div className="text-center text-yellow-400 text-4xl font-bold mb-6">
                ⭐ Spotlight Videos
              </div>

              <div className="flex flex-wrap justify-center gap-10">
                {spotlightVideos.map((video, index) => {
                  const id = getYouTubeId(video.url);
                  return (
                    <div
                      key={index}
                      className="relative border-4 border-yellow-400 rounded-2xl overflow-hidden bg-black"
                    >
                      <div className="absolute top-2 right-2 text-white bg-black bg-opacity-60 px-2 rounded text-sm">
                        {formatDate(video.createdAt)}
                      </div>
                      {id && (
                        <iframe
                          width="300"
                          height="200"
                          src={`https://www.youtube.com/embed/${id}`}
                          title={video.title}
                          allowFullScreen
                        ></iframe>
                      )}
                      <div className="p-2 text-white text-center">
                        <h3 className="font-bold">{video.title}</h3>
                        <p className="text-sm">{video.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* === ALL VIDEOS === */}
          <div className="content my-8 justify-start items-center sm:w-[95%] lg:w-[80%] sm:px-4 lg:px-10 pt-10 pb-10 black-bg border-black border-8">
            <div className="flex justify-between items-center px-10 mb-8">
              <div className="text-4xl font-extrabold stats-font text-white">
                All Videos
              </div>

              {/* ✅ Spotlight Controls */}
              <div className="flex gap-4">
                <Button
                  variant={editSpotlights ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setEditSpotlights(!editSpotlights)}
                >
                  {editSpotlights ? "Done" : "Edit Spotlights"}
                </Button>

                {editSpotlights && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={resetSpotlights}
                  >
                    Reset Spotlights
                  </Button>
                )}
              </div>
            </div>

            <div className="w-full flex justify-center">
              <div className="flex overflow-x-auto gap-10 scrollbar-thin scrollbar-thumb-slate-500 flex-wrap justify-center">
                {videos.map((video, index) => {
                  const id = getYouTubeId(video.url);
                  return (
                    <div
                      key={index}
                      className={`relative border-4 rounded-2xl overflow-hidden bg-black transition-all duration-200 ${
                        video.spotlight ? "border-yellow-400" : "border-white"
                      }`}
                    >
                      {/* ✅ Checkbox in spotlight edit mode */}
                      {editSpotlights && (
                        <div className="absolute top-2 left-2 bg-white rounded-md px-1">
                          <Checkbox
                            checked={video.spotlight}
                            onChange={(e) =>
                              toggleSpotlight(video._id, e.target.checked)
                            }
                          />
                        </div>
                      )}
                      <div className="absolute top-2 right-2 text-white bg-black bg-opacity-60 px-2 rounded text-sm">
                        {formatDate(video.createdAt)}
                      </div>
                      {id && (
                        <iframe
                          width="300"
                          height="200"
                          src={`https://www.youtube.com/embed/${id}`}
                          title={video.title}
                          allowFullScreen
                        ></iframe>
                      )}
                      <div className="p-2 text-white text-center">
                        <h3 className="font-bold">{video.title}</h3>
                        <p className="text-sm">{video.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* === DELETE VIDEO === */}
          <div className="content my-8 justify-start items-center sm:w-[95%] lg:w-[80%] sm:px-4 lg:px-10 pt-10 pb-10 black-bg border-black border-8">
            <div className="relative text-center w-fit mb-8 text-4xl font-extrabold stats-font p-2">
              Delete YouTube Video
              <span className="absolute w-[100%] h-1 bottom-0 left-[-50%] translate-x-[50%] rounded-full bg-white"></span>
            </div>

            <div className="w-full flex justify-center items-center">
              <FormControl sx={{ m: 1, minWidth: 200 }}>
                <InputLabel id="video-label">Videos</InputLabel>
                <Select
                  labelId="video-label"
                  label="Video"
                  onChange={(e) => setSelectedVideo(e.target.value)}
                  value={selectedVideo}
                >
                  {videos.map((video, index) => (
                    <MenuItem value={video._id} key={index}>
                      {video.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                color="error"
                variant="contained"
                onClick={deleteVideo}
                disabled={selectedVideo === ""}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosAdmin;
